import { useMutation, useQuery } from '@tanstack/react-query'

import type {
  AuthAccessTokenResponse,
  CheckoutRegisterRequest,
  CreateUserRequest,
  GetMeResponse,
  GetMyPassengerResponse,
  LoginWithEmailRequest,
  LoginWithEmailResponse,
  LogoutResponse,
  ReservationAccessRequest,
  ReservationAccessResponse,
  ResetPasswordRequest,
  SendResetPasswordEmailRequest,
} from '../types/index.js'

import { apiClient } from '../lib/client.js'
import { meKeys } from './me/keys.js'

export const useGetMeQuery = (enabled = true) =>
  useQuery({
    enabled,
    queryFn: async () => {
      const response = await apiClient.client.get<GetMeResponse>('/me')
      return response.data
    },
    queryKey: meKeys.me,
    staleTime: 5 * 60 * 1000,
  })

export const useGetMyPassengersQuery = (enabled = true) =>
  useQuery({
    enabled,
    queryFn: async () => {
      const response = await apiClient.client.get<GetMyPassengerResponse[]>('/me/passengers')
      return response.data
    },
    queryKey: meKeys.passengers,
    staleTime: 5 * 60 * 1000,
  })

export const useCreateUserMutation = () =>
  useMutation({
    mutationFn: async (data: CreateUserRequest) => {
      const response = await apiClient.client.post<string>('/auth/register', data)
      return response.data
    },
  })

export const useSendResetPasswordEmailMutation = () =>
  useMutation({
    mutationFn: async (data: SendResetPasswordEmailRequest) => {
      const response = await apiClient.client.post<string>('/auth/send-reset-password-email', data)
      return response.data
    },
  })

export const useResetPasswordMutation = () =>
  useMutation({
    mutationFn: async (data: ResetPasswordRequest) => {
      const response = await apiClient.client.post<string>('/auth/reset-password', data)
      return response.data
    },
  })

export const useLoginWithEmailMutation = () =>
  useMutation({
    mutationFn: async (data: LoginWithEmailRequest) => {
      const response = await apiClient.client.post<LoginWithEmailResponse>('/auth/login', data)
      return response.data
    },
  })

export const useReservationAccessMutation = () =>
  useMutation({
    mutationFn: async (data: ReservationAccessRequest) => {
      const response = await apiClient.client.post<ReservationAccessResponse>(
        '/auth/reservation-access',
        data
      )
      return response.data
    },
  })

export const useRefreshUserSessionMutation = () =>
  useMutation({
    mutationFn: async () => {
      const response = await apiClient.client.post<AuthAccessTokenResponse>('/auth/refresh')
      return response.data
    },
  })

export const useCheckoutRegisterMutation = () =>
  useMutation({
    mutationFn: async (data: CheckoutRegisterRequest) => {
      const response = await apiClient.client.post<LoginWithEmailResponse>(
        '/auth/checkout-register',
        data
      )
      return response.data
    },
  })

export const useLogoutUserMutation = () =>
  useMutation({
    mutationFn: async () => {
      const response = await apiClient.client.post<LogoutResponse>('/auth/logout')
      return response.data
    },
  })
